import axiosInstance from "services/common/axiosInstance";

const handleRequest = async <T>(promise: Promise<any>): Promise<T> => {
  try {
    const response = await promise;
    return response.data;
  } catch (error: any) {
    throw error?.response?.data || error.message;
  }
};

const BASE_URL = "/rbac";

export interface AddUserTypeParams {
  usertypeid: number;
  usertypename: string;
  description: string;
}

export interface AddUserTypeResponse {
  message: string;
  data: number; 
  success: boolean;
}

export interface UpdateUserTypeParams {
  usertypename: string;
  description: string;
  status: number; 
}

export interface UpdateUserTypeResponse {
  message: string;
  data?: any;
  success: boolean;
}
export interface UserType {
  usertypeid: number;
  usertypename: string;
  description: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface ListUserTypeResponse {
  message: string;
  data: UserType[];
  success: boolean;
}
export interface AddRolesParams {
  roleid: number;
  rolename: string;
  description: string;
}

export interface AddRolesResponse {
  message: string;
  data: number; 
  success: boolean;
}
export interface UpdateRolesParams {
  rolename: string;
  description: string;
  status: number; 
}

export interface UpdateRolesResponse {
  message: string;
  data?: any;
  success: boolean;
}

export interface Roles {
  roleid: number;
  rolename: string;
  description: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface DeleteRolesParams {
  status: number;
}

export interface DeleteRolesResponse {
  message: string;
  data?: any;
  success: boolean;
}

export interface ListRolesResponse {
  message: string;
  data: Roles[];
  success: boolean;
}

export interface AddPermissionParams {
  permissionid: number;
  permissionname: string;
  description: string;
}

export interface AddPermissionResponse {
  message: string;
  data: number; 
  success: boolean;
}

export interface UpdatePermissionParams {
  permissionname: string;
  description: string;
  status: number; 
}

export interface UpdatePermissionResponse {
  message: string;
  data?: any;
  success: boolean;
}

export interface Permission {
  permissionid: number;
  permissionname: string;
  description: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface DeletePermissionParams {
  status: number;
}

export interface DeletePermissionResponse {
  message: string;
  data?: any;
  success: boolean;
}

export interface ListPermissionResponse {
  message: string;
  data: Permission[];
  success: boolean;
}

export interface AddRolePermissionParams {
  permissionid: number;
  roleid: number;
}

export interface AddRolePermissionResponse {
  message: string;
  data: number;
  success: boolean;
}

export interface RolePermission {
  rolepermissionid: number;
  permissionname: string;
  rolename: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface ListRolePermissionResponse {
  message: string;
  data: RolePermission[];
  success: boolean;
}

export interface DeleteRolePermissionResponse {
  message: string;
  data?: any;
  success: boolean;
}

export const addUserType = (data: AddUserTypeParams) =>
  handleRequest<AddUserTypeResponse>(
    axiosInstance.post(`${BASE_URL}/addusertype`, data)
  );

export const updateUserType = (usertypeid: number, data: UpdateUserTypeParams) =>
  handleRequest<UpdateUserTypeResponse>(
    axiosInstance.put(`${BASE_URL}/updateusertype?usertypeid=${usertypeid}`, data)
  );

export const listUserType = () =>
  handleRequest<ListUserTypeResponse>(
    axiosInstance.get(`${BASE_URL}/listusertype`)
  );

export const addRoles = (data: AddRolesParams) =>
  handleRequest<AddRolesResponse>(
    axiosInstance.post(`${BASE_URL}/addroles`, data)
  );

export const updateRoles = (roleid: number, data: UpdateRolesParams) =>
  handleRequest<UpdateRolesResponse>(
    axiosInstance.put(`${BASE_URL}/updateroles?roleid=${roleid}`, data)
  );

export const deleteRoles = (roleid: number, data: DeleteRolesParams) =>
  handleRequest<DeleteRolesResponse>(
    axiosInstance.delete(`${BASE_URL}/deleteroles`, {
      params: { roleid },
      data,
    })
  );

export const listRoles = () =>
  handleRequest<ListRolesResponse>(
    axiosInstance.get(`${BASE_URL}/listroles`)
  );

export const addPermission = (data: AddPermissionParams) =>
  handleRequest<AddPermissionResponse>(
    axiosInstance.post(`${BASE_URL}/addpermission`, data)
  );

export const updatePermission = (permissionid: number, data: UpdatePermissionParams) =>
  handleRequest<UpdatePermissionResponse>(
    axiosInstance.put(`${BASE_URL}/updatepermission?permissionid=${permissionid}`, data)
  );

export const deletePermission = (permissionid: number, data: DeletePermissionParams) =>
  handleRequest<DeletePermissionResponse>(
    axiosInstance.delete(`${BASE_URL}/deletepermission`, {
      params: { permissionid },
      data,
    })
  );

export const listPermissions = () =>
  handleRequest<ListPermissionResponse>(
    axiosInstance.get(`${BASE_URL}/listpermissions`)
  );

export const addRolePermission = (data: AddRolePermissionParams) =>
  handleRequest<AddRolePermissionResponse>(
    axiosInstance.post(`${BASE_URL}/addrolepermission`, data)
  );

export const listRolePermission = () =>
  handleRequest<ListRolePermissionResponse>(
    axiosInstance.get(`${BASE_URL}/listrolepermission`)
  );

export const deleteRolePermission = (rolepermissionid: number) =>
  handleRequest<DeleteRolePermissionResponse>(
    axiosInstance.delete(`${BASE_URL}/deleterolepermission`, {
      params: { rolepermissionid },
    })
  );

export interface AddNewAdminUserParams {
  usertypeid: number;
  username: string;
  password: string;
  phone_no: string;
  emailid: string;
}

export interface AddNewAdminUserResponse {
  message: string;
  data?: any;
  success: boolean;
}

export const addNewAdminUser = (data: AddNewAdminUserParams) =>
  handleRequest<AddNewAdminUserResponse>(
    axiosInstance.post("/adminauth/addnewadminuser", data)
  );

export interface ListAdminUserParams {
  page: number;
  per_page: number;
  usermasterid?: number;
  usertypeid?: number;
  username?: string | null;
  status?: number;
  email_id?: string | null;
  phone_no?: string | null;
}

export interface AdminUser {
  usermasterid: number;
  username: string;
  email_id: string;
  phone_no: string;
  application_name: string;
  user_type_name: string;
  status: string;
  createdon: string;
  createdby: string;
  updatedon: string | null;
  updatedby: string | null;
}

export interface ListAdminUserData {
  users: AdminUser[];
  pagination: {
    page: number;
    per_page: number;
    total_pages: number;
    total_items: number;
  };
}

export interface ListAdminUserResponse {
  message: string;
  data: {
    status: string;
    message: string;
    data: ListAdminUserData;
  };
  success: boolean;
}

export const listAdminUser = (data: ListAdminUserParams) =>
  handleRequest<ListAdminUserResponse>(
    axiosInstance.post("/adminauth/listadminuser", data)
  );

export interface AddUserRoleParams {
  usermasterid: number;
  roleid: number;
}

export interface AddUserRoleResponse {
  message: string;
  data: number;
  success: boolean;
}

export interface UserRole {
  userroleid: number;
  username: string;
  email_id: string;
  phone_no: string;
  rolename: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface ListUserRoleResponse {
  message: string;
  data: UserRole[];
  success: boolean;
}

export interface DeleteUserRoleResponse {
  message: string;
  data?: any;
  success: boolean;
}

export const addUserRole = (data: AddUserRoleParams) =>
  handleRequest<AddUserRoleResponse>(
    axiosInstance.post(`${BASE_URL}/adduserrole`, data)
  );

export const listUserRole = (usermaster_id: number) =>
  handleRequest<ListUserRoleResponse>(
    axiosInstance.get(`${BASE_URL}/listuserrole`, {
      params: { usermaster_id },
    })
  );

export const deleteUserRole = (userroleid: number) =>
  handleRequest<DeleteUserRoleResponse>(
    axiosInstance.delete(`${BASE_URL}/deleteuserrole`, {
      params: { userroleid },
    })
  );

export interface AddApplicationParams {
  applicationid: number;
  organisationid: number;
  applicationname: string;
  applicationdescription: string;
}

export interface AddApplicationResponse {
  message: string;
  data?: any;
  success: boolean;
}

export interface Application {
  applicationid: number;
  organisationid: number;
  applicationtoken: string;
  applicationname: string;
  applicationdescription: string;
  tokencreateddate: string;
  tokenexpiredate: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface ListApplicationsResponse {
  message: string;
  data: Application[];
  success: boolean;
}

export const addApplication = (data: AddApplicationParams) =>
  handleRequest<AddApplicationResponse>(
    axiosInstance.post(`${BASE_URL}/addapplications`, data)
  );

export const listApplications = () =>
  handleRequest<ListApplicationsResponse>(
    axiosInstance.get(`${BASE_URL}/listapplications`)
  );

// Menu interfaces and services
export interface AddMenuParams {
  moduleiid: number;
  parentid: number;
  title: string;
  description: string;
  routepath: string;
  displayorder: number;
}

export interface AddMenuResponse {
  message: string;
  data: number; // menuid
  success: boolean;
}

export interface UpdateMenuParams {
  moduleiid: number;
  parentid: number;
  title: string;
  description: string;
  routepath: string;
  displayorder: number;
  status?: number;
}

export interface UpdateMenuResponse {
  message: string;
  data?: any;
  success: boolean;
}

export interface Menu {
  menuid: number;
  moduleiid: number;
  parentid: number;
  applicationname: string;
  modulename: string;
  title: string;
  description: string;
  routepath: string;
  displayorder: number;
  status: string;
  createdon: string;
  createdby: string;
  updatedon: string | null;
  updatedby: string | null;
}

export interface ListMenusResponse {
  message: string;
  data: Menu[];
  success: boolean;
}

export interface ListMenusParams {
  parentid: number;
  moduleid: number;
  menuid: number;
  status: number;
}

export interface DeleteMenuResponse {
  message: string;
  data?: any;
  success: boolean;
}

export const addMenu = (data: AddMenuParams) =>
  handleRequest<AddMenuResponse>(
    axiosInstance.post(`${BASE_URL}/addmenu`, data)
  );

export const listMenus = (data: ListMenusParams) =>
  handleRequest<ListMenusResponse>(
    axiosInstance.post(`${BASE_URL}/listmenus`, data)
  );

export const updateMenu = (menuid: number, data: UpdateMenuParams) =>
  handleRequest<UpdateMenuResponse>(
    axiosInstance.put(`${BASE_URL}/updatemenu?menuid=${menuid}`, data)
  );

export const deleteMenu = (menuid: number) =>
  handleRequest<DeleteMenuResponse>(
    axiosInstance.delete(`${BASE_URL}/deletemenu?menuid=${menuid}`)
  );

// Module interfaces and services
export interface AddModuleParams {
  moduleid: number;
  applicationid: number;
  modulename: string;
  description: string;
}

export interface AddModuleResponse {
  message: string;
  data: number; // moduleid
  success: boolean;
}

export interface Module {
  moduleid: number;
  applicationid: number;
  applicationname: string;
  modulename: string;
  description: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface ListModulesResponse {
  message: string;
  data: Module[];
  success: boolean;
}

export interface DeleteModuleResponse {
  message: string;
  data?: any;
  success: boolean;
}

export const addModule = (data: AddModuleParams) =>
  handleRequest<AddModuleResponse>(
    axiosInstance.post(`${BASE_URL}/addmodule`, data)
  );

export const listModules = (applicationid: number) =>
  handleRequest<ListModulesResponse>(
    axiosInstance.get(`${BASE_URL}/listmodule`, {
      params: { applicationid },
    })
  );

export const deleteModule = (moduleid: number) =>
  handleRequest<DeleteModuleResponse>(
    axiosInstance.delete(`${BASE_URL}/deletemodule`, {
      params: { moduleid },
    })
  );

export interface AddTaskParams {
  id: number;
  taskname: string;
  description: string;
}

export interface AddTaskResponse {
  message: string;
  data: number; 
  success: boolean;
}

export interface UpdateTaskParams {
  id: number;
  taskname: string;
  description: string;
  status: number;
}

export interface UpdateTaskResponse {
  message: string;
  data?: any;
  success: boolean;
}

export interface Task {
  taskid: number;
  taskname: string;
  description: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface ListTasksResponse {
  message: string;
  data: Task[];
  success: boolean;
}

export interface DeleteTaskResponse {
  message: string;
  data?: any;
  success: boolean;
}

export const addTask = (data: AddTaskParams) =>
  handleRequest<AddTaskResponse>(
    axiosInstance.post(`${BASE_URL}/addtask`, data)
  );

export const updateTask = (taskid: number, data: UpdateTaskParams) =>
  handleRequest<UpdateTaskResponse>(
    axiosInstance.put(`${BASE_URL}/updatetask?taskid=${taskid}`, data)
  );

export const listTasks = () =>
  handleRequest<ListTasksResponse>(
    axiosInstance.get(`${BASE_URL}/listtask`)
  );

export const deleteTask = (taskid: number) =>
  handleRequest<DeleteTaskResponse>(
    axiosInstance.delete(`${BASE_URL}/deletetask`, {
      params: { taskid },
    })
  );

// Role Task interfaces and services
export interface AddRoleTaskParams {
  roleid: number;
  taskid: number;
}

export interface AddRoleTaskResponse {
  message: string;
  data: number; // roletaskid
  success: boolean;
}

export interface ListRoleTaskParams {
  roleid: number;
  taskid: number;
  status: number;
}

export interface RoleTask {
  id: number;
  roleid: number;
  taskid: number;
  rolename: string;
  taskname: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface ListRoleTaskResponse {
  message: string;
  data: RoleTask[];
  success: boolean;
}

export interface DeleteRoleTaskResponse {
  message: string;
  data?: any;
  success: boolean;
}

export const addRoleTask = (data: AddRoleTaskParams) =>
  handleRequest<AddRoleTaskResponse>(
    axiosInstance.post(`${BASE_URL}/addroletask`, data)
  );

export const listRoleTask = (data: ListRoleTaskParams) =>
  handleRequest<ListRoleTaskResponse>(
    axiosInstance.post(`${BASE_URL}/listroletask`, data)
  );

export const deleteRoleTask = (roletaskid: number) =>
  handleRequest<DeleteRoleTaskResponse>(
    axiosInstance.delete(`${BASE_URL}/deleteroletask`, {
      params: { roletaskid },
    })
  );

// Menu Permission interfaces and services
export interface AddMenuPermissionParams {
  menuid: number;
  permissionid: number;
}

export interface AddMenuPermissionResponse {
  message: string;
  data: number; // menupermissionid
  success: boolean;
}

export interface ListMenuPermissionParams {
  menuid: number;
  permissionid: number;
  status: number;
}

export interface MenuPermission {
  menupermissionid: number;
  menuid: number;
  permissionid: number;
  applicationname: string;
  modulename: string;
  menuname: string;
  permissionname: string;
  status: string;
  createdon: string;
  createdby: string;
}

export interface ListMenuPermissionResponse {
  message: string;
  data: MenuPermission[];
  success: boolean;
}

export interface DeleteMenuPermissionResponse {
  message: string;
  data?: any;
  success: boolean;
}

export const addMenuPermission = (data: AddMenuPermissionParams) =>
  handleRequest<AddMenuPermissionResponse>(
    axiosInstance.post(`${BASE_URL}/addmenupermission`, data)
  );

export const listMenuPermission = (data: ListMenuPermissionParams) =>
  handleRequest<ListMenuPermissionResponse>(
    axiosInstance.post(`${BASE_URL}/listmenupermission`, data)
  );

export const deleteMenuPermission = (menupermissionid: number) =>
  handleRequest<DeleteMenuPermissionResponse>(
    axiosInstance.delete(`${BASE_URL}/deletemenupermission`, {
      params: { menupermissionid },
    })
  );